/**
 * Wishlist location dropdowns (Country, State, City) with the same white panel as the Sort menu,
 * so they look the same on phones, tablets and desktop instead of opening the phone's own picker.
 *
 * Each native <select> stays in place as the source of truth (s-wishlist.js fills, reads and
 * validates it). This script hides it visually, adds a trigger button that looks like the field,
 * and a list of option buttons rebuilt from the select every time it opens. Picking an option sets
 * the select's value and fires a bubbling `change`, exactly like a native pick.
 *
 * Mirrors the select's disabled and hidden state (city hides when "Other" shows the text box).
 */
(function () {
  var SELECTORS = '[data-wishlist-loc="country"], [data-wishlist-loc="state"], [data-wishlist-city-select]';
  var CHEVRON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-full"><path d="M6 9l6 6 6-6"/></svg>';
  var CHECK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-full"><polyline points="20 6 9 17 4 12"/></svg>';
  var count = 0;
  var openOne = null;
  var all = [];

  function enhance(select) {
    if (select.dataset.prettySelect) return;
    select.dataset.prettySelect = '1';
    var n = ++count;

    var wrap = document.createElement('div');
    wrap.className = 'relative mt-1';
    var button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', 'loc-panel-' + n);
    button.className = 'flex h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-input border border-border bg-surface px-3 text-start text-base sm:text-sm text-on-surface focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60 aria-expanded:border-on-surface';
    var label = document.createElement('span');
    label.className = 'min-w-0 truncate leading-snug';
    var arrow = document.createElement('span');
    arrow.className = 'inline-flex size-4 shrink-0 transition-transform duration-300';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML = CHEVRON;
    button.appendChild(label);
    button.appendChild(arrow);

    var panel = document.createElement('div');
    panel.id = 'loc-panel-' + n;
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;
    panel.className = 'absolute inset-x-0 top-full z-40 mt-1 max-h-64 overflow-y-auto overscroll-contain border border-on-surface bg-surface';

    wrap.appendChild(button);
    wrap.appendChild(panel);
    // The button goes first so tapping the field's label opens the list.
    select.parentNode.insertBefore(wrap, select);

    // Keep the select for data and validation, out of sight and out of the tab order.
    select.classList.add('sr-only');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    function current() {
      return select.options[select.selectedIndex] || null;
    }
    function paint() {
      var opt = current();
      var text = opt ? opt.textContent : '';
      if (label.textContent !== text) label.textContent = text;
      label.classList.toggle('text-on-surface-secondary', !select.value);
      button.disabled = select.disabled;
      wrap.hidden = select.hidden;
      if ((select.disabled || select.hidden) && !panel.hidden) close(false);
    }
    function build() {
      panel.textContent = '';
      for (var i = 0; i < select.options.length; i++) {
        var o = select.options[i];
        // Skip the "Select ..." placeholder; the field itself shows it.
        if (o.disabled || (!o.value && select.options.length > 1)) continue;
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'option');
        b.dataset.index = String(i);
        var on = i === select.selectedIndex && !!o.value;
        b.setAttribute('aria-selected', String(on));
        b.className = 'group/opt flex min-h-12 w-full cursor-pointer items-center justify-between gap-3 border-0 border-b border-border bg-transparent px-4 py-2 text-start text-sm font-normal leading-snug text-on-surface last:border-b-0 transition-colors duration-200 hover:bg-on-surface hover:text-surface focus-visible:bg-on-surface focus-visible:text-surface focus-visible:outline-none';
        var t = document.createElement('span');
        t.textContent = o.textContent;
        var c = document.createElement('span');
        c.className = 'inline-flex size-3.5 shrink-0 opacity-0 group-aria-selected/opt:opacity-100';
        c.setAttribute('aria-hidden', 'true');
        c.innerHTML = CHECK;
        b.appendChild(t);
        b.appendChild(c);
        panel.appendChild(b);
      }
    }
    function open() {
      if (button.disabled) return;
      if (openOne && openOne !== api) openOne.close(false);
      build();
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      arrow.classList.add('rotate-180');
      openOne = api;
      var chosen = panel.querySelector('[aria-selected="true"]') || panel.firstElementChild;
      if (chosen) {
        panel.scrollTop = Math.max(0, chosen.offsetTop - panel.clientHeight / 2 + chosen.offsetHeight / 2);
        chosen.focus({ preventScroll: true });
      }
    }
    function close(refocus) {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      arrow.classList.remove('rotate-180');
      if (openOne === api) openOne = null;
      if (refocus) button.focus();
    }
    var api = { close: close, wrap: wrap, paint: paint };
    all.push(api);

    button.addEventListener('click', function () {
      if (panel.hidden) open(); else close(false);
    });
    panel.addEventListener('click', function (event) {
      var b = event.target.closest('[role="option"]');
      if (!b) return;
      var i = Number(b.dataset.index);
      if (select.selectedIndex !== i) {
        select.selectedIndex = i;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      paint();
      close(true);
    });
    wrap.addEventListener('keydown', function (event) {
      if (panel.hidden) {
        if (event.target === button && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          event.preventDefault();
          open();
        }
        return;
      }
      var options = Array.prototype.slice.call(panel.querySelectorAll('[role="option"]'));
      var at = options.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (options.length) options[(at + 1) % options.length].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (options.length) options[(at - 1 + options.length) % options.length].focus();
      } else if (event.key === 'Tab') {
        close(false);
      } else if (event.key.length === 1) {
        // Type a letter to jump to the first option starting with it.
        var k = event.key.toLowerCase();
        for (var j = 0; j < options.length; j++) {
          var idx = (at + 1 + j) % options.length;
          if (options[idx].textContent.trim().toLowerCase().indexOf(k) === 0) { options[idx].focus(); break; }
        }
      }
    });
    // Anything that focuses the hidden select (its label, the "please choose" check) lands on the button.
    select.addEventListener('focus', function () { if (!button.disabled) button.focus(); });
    select.addEventListener('change', paint);
    new MutationObserver(paint).observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden'] });
    paint();
    // Values set by script without a change event are picked up shortly after.
    setTimeout(paint, 0);
  }

  document.addEventListener('pointerdown', function (event) {
    if (openOne && !openOne.wrap.contains(event.target)) openOne.close(false);
  });

  function run() {
    var list = document.querySelectorAll(SELECTORS);
    for (var i = 0; i < list.length; i++) enhance(list[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  // Saved values restored by script (no change event) show up within a moment while the fields are on screen.
  setInterval(function () {
    for (var i = 0; i < all.length; i++) if (all[i].wrap.offsetParent || all[i].wrap.hidden) all[i].paint();
  }, 400);
})();
